import { repositoryDelivery } from "../_base/_const/_constVar";
import { MessageResponse } from "../model/MessageResponse";
import { NotificationExtension } from "../_base/extension/NotificationExtension";

// Interface for product specifications
export interface ProductSpecification {
  id: number;
  itemCode: string;
  itemName: string;
  weight: number; // in grams
  length: number; // in cm
  width: number; // in cm
  height: number; // in cm
  isCongKenh?: boolean;
}

// Interface for product with quantity from order
export interface ProductWithQuantity {
  id?: number;
  itemId?: number;
  itemCode?: string;
  itemName?: string;
  quality?: number;
  quantity?: number;
}

// Interface for oversized item details
export interface OversizedItemDetail {
  itemId: number;
  itemCode: string;
  itemName: string;
  weight: number; // in kg
  length: number;
  width: number;
  height: number;
  volume: number; // in cubic cm
  quantity: number;
  totalWeight: number; // in kg
  totalVolume: number; // in cubic cm
}

// Interface for specifications check result
export interface SpecificationsCheckResult {
  hasOverSizeItems: boolean;
  overSizeItemsDetails: OversizedItemDetail[];
  totalWeight: number; // in kg
  maxDimension: number; // in cm
  totalCodAmount?: number; // for future COD validation
}

// Configuration for oversized thresholds
export const OVERSIZED_THRESHOLDS = {
  WEIGHT_KG: 50, // More than 50kg per item
  LENGTH_CM: 200, // More than 200cm length
  WIDTH_CM: 150, // More than 150cm width
  HEIGHT_CM: 150, // More than 150cm height
  VOLUME_CM3: 1000000, // More than 1 cubic meter (1,000,000 cm³)
};

// Configuration for COD validation (for future use)
export const COD_VALIDATION_CONFIG = {
  MAX_COD_AMOUNT: 50000000, // 50 million VND
  MIN_COD_AMOUNT: 0,
  // Add more COD validation rules here
};

/**
 * Convert weight from grams to kilograms
 * @param weightInGrams Weight in grams
 * @returns Weight in kilograms (rounded to 3 decimal places)
 */
export const convertGramsToKg = (weightInGrams: number): number => {
  return Math.round((weightInGrams / 1000) * 1000) / 1000; // Convert and round to 3 decimal places
};

/**
 * Check if an item is oversized based on configured thresholds
 * @param weight Weight in kg
 * @param length Length in cm
 * @param width Width in cm
 * @param height Height in cm
 * @param volume Volume in cubic cm
 * @returns True if item is oversized
 */
export const isItemOversized = (
  weight: number,
  length: number,
  width: number,
  height: number,
  volume: number
): boolean => {
  return (
    weight > OVERSIZED_THRESHOLDS.WEIGHT_KG ||
    length > OVERSIZED_THRESHOLDS.LENGTH_CM ||
    width > OVERSIZED_THRESHOLDS.WIDTH_CM ||
    height > OVERSIZED_THRESHOLDS.HEIGHT_CM ||
    volume > OVERSIZED_THRESHOLDS.VOLUME_CM3
  );
};

/**
 * Calculate volume in cubic centimeters
 * @param length Length in cm
 * @param width Width in cm
 * @param height Height in cm
 * @returns Volume in cubic cm
 */
export const calculateVolume = (
  length: number,
  width: number,
  height: number
): number => {
  return length * width * height;
};

/**
 * Find the maximum dimension among length, width, and height
 * @param length Length in cm
 * @param width Width in cm
 * @param height Height in cm
 * @returns Maximum dimension in cm
 */
export const getMaxDimension = (
  length: number,
  width: number,
  height: number
): number => {
  return Math.max(length, width, height);
};

/**
 * Validate COD amount (for future use)
 * @param codAmount COD amount in VND
 * @returns Validation result
 */
export const validateCodAmount = (
  codAmount: number
): { isValid: boolean; message?: string } => {
  if (codAmount < COD_VALIDATION_CONFIG.MIN_COD_AMOUNT) {
    return {
      isValid: false,
      message: `Số tiền COD không được nhỏ hơn ${COD_VALIDATION_CONFIG.MIN_COD_AMOUNT.toLocaleString()} VND`,
    };
  }

  if (codAmount > COD_VALIDATION_CONFIG.MAX_COD_AMOUNT) {
    return {
      isValid: false,
      message: `Số tiền COD không được vượt quá ${COD_VALIDATION_CONFIG.MAX_COD_AMOUNT.toLocaleString()} VND`,
    };
  }

  return { isValid: true };
};

/**
 * Fetch product specifications from API
 * @param productIds Array of product IDs
 * @returns Promise<ProductSpecification[]>
 */
export const fetchProductSpecifications = async (
  productIds: (number | string)[]
): Promise<ProductSpecification[]> => {
  if (!productIds || productIds.length === 0) {
    console.log("No product IDs provided for specifications check");
    return [];
  }

  try {
    // Build query parameters for the API call
    const queryParams = new URLSearchParams();
    productIds.forEach((id) => {
      queryParams.append("Ids", id.toString());
    });

    console.log(
      "Calling specifications API with query:",
      queryParams.toString()
    );

    // Call the specifications API
    const response = await repositoryDelivery.get<
      MessageResponse<ProductSpecification[]>
    >(`/api/v1/TblItem/get-item-specifications?${queryParams.toString()}`);

    console.log("Specifications API response:", response);

    if (response && response.success && response.data) {
      return response.data;
    } else {
      console.warn("Specifications API returned no data or error:", response);
      return [];
    }
  } catch (error) {
    console.error("Error fetching product specifications:", error);
    throw error;
  }
};

/**
 * Process product specifications and calculate totals
 * @param specifications Array of product specifications from API
 * @param productsInOrder Array of products in the order with quantities
 * @param codAmount Optional COD amount for validation
 * @returns SpecificationsCheckResult
 */
export const processProductSpecifications = (
  specifications: ProductSpecification[],
  productsInOrder: ProductWithQuantity[],
  codAmount?: number
): SpecificationsCheckResult => {
  console.log("Processing product specifications:", specifications);
  console.log("Products in order:", productsInOrder);

  let totalWeight = 0; // in kg
  const overSizeItems: OversizedItemDetail[] = [];
  let maxDimension = 0;

  // Process each product specification
  specifications.forEach((spec) => {
    // Convert weight from grams to kg
    const weightInKg = convertGramsToKg(
      parseFloat(spec.weight?.toString() || "0")
    );
    const length = parseFloat(spec.length?.toString() || "0");
    const width = parseFloat(spec.width?.toString() || "0");
    const height = parseFloat(spec.height?.toString() || "0");

    // Calculate volume (L x W x H) in cubic centimeters
    const volume = calculateVolume(length, width, height);

    // Find the corresponding product in order to get quantity
    // Match using spec.id with item.itemId (product ID)
    const product = productsInOrder.find(
      (item) => item?.itemId === spec.id
    );
    const quantity = product?.quality || product?.quantity || 1;

    // Add to totals (multiply by quantity)
    totalWeight += weightInKg * quantity;

    // Find maximum dimension (largest single dimension)
    const currentMaxDimension = getMaxDimension(length, width, height);
    if (currentMaxDimension > maxDimension) {
      maxDimension = currentMaxDimension;
    }

    // TẠM ĐÓNG LOGIC CŨ: Không dùng ngưỡng cứng (OVERSIZED_THRESHOLDS) nữa để chuyển sang dùng data API
    // const isOverSize = isItemOversized(
    //   weightInKg,
    //   length,
    //   width,
    //   height,
    //   volume
    // );

    // LOGIC MỚI: Đọc trực tiếp cờ isCongKenh từ API trả về.
    const isOverSize = spec.isCongKenh === true;

    if (isOverSize) {
      overSizeItems.push({
        itemId: spec.id,
        itemCode: spec.itemCode,
        itemName: spec.itemName,
        weight: weightInKg, // in kg
        length: length,
        width: width,
        height: height,
        volume: volume,
        quantity: quantity,
        totalWeight: weightInKg * quantity, // in kg
        totalVolume: volume * quantity,
      });
    }

    console.log(
      `Product ${spec.itemCode}: Weight=${weightInKg}kg (${spec.weight}g), Quantity=${quantity}, MaxDim=${currentMaxDimension}cm, IsOverSize=${isOverSize}`
    );
  });

  const result: SpecificationsCheckResult = {
    hasOverSizeItems: overSizeItems.length > 0,
    overSizeItemsDetails: overSizeItems,
    totalWeight: totalWeight,
    maxDimension: maxDimension,
  };

  // Add COD validation if amount is provided
  if (codAmount !== undefined) {
    result.totalCodAmount = codAmount;
    const codValidation = validateCodAmount(codAmount);
    if (!codValidation.isValid) {
      console.warn("COD validation failed:", codValidation.message);
      // You can add more handling here, such as adding to oversized items or creating a separate validation result
    }
  }

  console.log("Specifications processing result:", result);
  return result;
};

/**
 * Main function to check product specifications
 * @param productsInOrder Array of products in the order
 * @param codAmount Optional COD amount for validation
 * @returns Promise<SpecificationsCheckResult>
 */
export const checkProductSpecifications = async (
  productsInOrder: ProductWithQuantity[],
  codAmount?: number
): Promise<SpecificationsCheckResult> => {
  console.log("checkProductSpecifications function started");
  if (!productsInOrder || productsInOrder.length === 0) {
    console.log("No products in order to check specifications");
    return {
      hasOverSizeItems: false,
      overSizeItemsDetails: [],
      totalWeight: 0,
      maxDimension: 0,
    };
  }

  try {
    // Extract product IDs from order
    const productIds = productsInOrder
      .map((item) => item?.itemId)
      .filter(
        (id): id is number => id !== undefined && id !== null && id !== 0
      );

    console.log("Product IDs to check specifications:", productIds);
    if (productIds.length === 0) {
      console.log("No valid product IDs found in order");
      return {
        hasOverSizeItems: false,
        overSizeItemsDetails: [],
        totalWeight: 0,
        maxDimension: 0,
      };
    }

    // Fetch specifications from API
    const specifications = await fetchProductSpecifications(productIds);

    // Process specifications and return result
    return processProductSpecifications(
      specifications,
      productsInOrder,
      codAmount
    );
  } catch (error) {
    console.error("Error checking product specifications:", error);
    NotificationExtension.Fails(
      "Không thể kiểm tra thông số sản phẩm. Vui lòng thử lại."
    );
    return {
      hasOverSizeItems: false,
      overSizeItemsDetails: [],
      totalWeight: 0,
      maxDimension: 0,
    };
  }
};
